/**
 * Shared onboard / edit payload builders for the ops console (ClubsView, etc.).
 */

import { PACE_RANGES } from "../constants/clubConstants";
import { CLUB_EDITABLE_LINK_FIELDS, getClubAuxiliaryLinkFieldKeys } from "./consoleUtils";

const AUX_LINK_FIELD_DEFAULTS = Object.fromEntries(getClubAuxiliaryLinkFieldKeys().map((k) => [k, ""]));

export const INTERNAL_NOTES_MAX_LENGTH = 2000;

export const ONBOARD_REQUIRED_FIELD_ERROR = "Email, password, club name, and city are required.";
export const ONBOARD_DERIVE_REQUIRED_FIELD_ERROR = "Club name and city are required.";
export const ONBOARD_PASSWORD_LENGTH_ERROR = "Password must be at least 8 characters.";
export const ONBOARD_CITY_ID_REQUIRED_ERROR =
  "Select a city from the console (URL ?city=…) before onboarding.";

/** When true, backend derives organizer email/password from run_club_name (must match server ONBOARD_DERIVE_ORGANIZER_CREDENTIALS). */
export function onboardDerivesOrganizerCredentials() {
  return String(process.env.REACT_APP_ONBOARD_DERIVE_ORGANIZER_CREDENTIALS || "")
    .toLowerCase()
    .trim() === "true";
}

/**
 * Shared prerequisites before POST /admin/onboard-club from console flows.
 * @param {object} form
 * @param {{ requireCityId?: boolean, cityId?: string | null }} [options]
 * @returns {{ ok: true } | { ok: false, errorMessage: string }}
 */
export function validateConsoleOnboardPrerequisites(form, options = {}) {
  const { requireCityId = false, cityId } = options;
  const run_club_name = (form?.run_club_name ?? "").trim();
  const city = (form?.city ?? "").trim();
  const derive = onboardDerivesOrganizerCredentials();

  if (!run_club_name || !city) {
    return {
      ok: false,
      errorMessage: derive ? ONBOARD_DERIVE_REQUIRED_FIELD_ERROR : ONBOARD_REQUIRED_FIELD_ERROR,
    };
  }

  if (!derive) {
    const email = (form?.email ?? "").trim().toLowerCase();
    const password = form?.password ?? "";
    if (!email || !password) {
      return { ok: false, errorMessage: ONBOARD_REQUIRED_FIELD_ERROR };
    }
    if (password.length < 8) {
      return { ok: false, errorMessage: ONBOARD_PASSWORD_LENGTH_ERROR };
    }
  }

  if (requireCityId && !(cityId != null && String(cityId).trim())) {
    return { ok: false, errorMessage: ONBOARD_CITY_ID_REQUIRED_ERROR };
  }
  return { ok: true };
}

/** Initial onboard form shape; same fields as EditClubCardScreen (mobile). */
export const INITIAL_ONBOARD_FORM = {
  email: "",
  password: "",
  run_club_name: "",
  city: "",
  state: "",
  country: "",
  type: "",
  description: "",
  skill_level: "All levels",
  home_base_address: "",
  home_base_description: "",
  latitude: null,
  longitude: null,
  paces: [],
  club_notes: ["", "", ""],
  recurring_runs_vary: false,
  internal_notes: "",
  strava_club_id: "",
  region: "",
  ...AUX_LINK_FIELD_DEFAULTS,
};

/**
 * Optional string fields for onboard payload; only included if non-empty after trim.
 * Also used by buildEditPayload in ClubsView (edit club PATCH).
 */
export const ONBOARD_OPTIONAL_FIELDS = [
  "state",
  "country",
  "type",
  "description",
  "skill_level",
  "home_base_address",
  "home_base_description",
  ...getClubAuxiliaryLinkFieldKeys(),
];

/**
 * @param {object} form
 * @param {string} [cityId]
 * @returns {object}
 */
export function buildOnboardPayload(form, cityId) {
  const run_club_name = (form.run_club_name ?? "").trim();
  const city = (form.city ?? "").trim();
  const derive = onboardDerivesOrganizerCredentials();
  const payload = { run_club_name, city };
  if (!derive) {
    payload.email = (form.email ?? "").trim().toLowerCase();
    payload.password = form.password ?? "";
  }
  const homeBase = (form.home_base_address ?? "").trim().slice(0, 200);
  ONBOARD_OPTIONAL_FIELDS.forEach((key) => {
    const value = (form[key] ?? "").trim();
    if (value) payload[key] = value;
  });
  const paceCodes = Array.isArray(form.paces) ? form.paces.filter(Boolean).slice(0, 3) : [];
  if (paceCodes.length > 0) {
    payload.paces = paceCodes
      .map((code) => PACE_RANGES.find((r) => r.value === code)?.label ?? (typeof code === "string" ? code : String(code)))
      .filter(Boolean);
  }
  const notes = Array.isArray(form.club_notes) ? form.club_notes.map((n) => (n ?? "").trim()).filter(Boolean).slice(0, 3) : [];
  if (notes.length > 0) payload.club_notes = notes;
  if (cityId) payload.city_id = cityId;
  payload.recurring_runs_vary = Boolean(form.recurring_runs_vary);
  const internalNotes = (form.internal_notes ?? "").trim().slice(0, INTERNAL_NOTES_MAX_LENGTH);
  if (internalNotes) payload.internal_notes = internalNotes;
  const lat = form.latitude != null ? Number(form.latitude) : null;
  const lon = form.longitude != null ? Number(form.longitude) : null;
  if (
    homeBase &&
    lat != null &&
    lon != null &&
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  ) {
    payload.latitude = lat;
    payload.longitude = lon;
  }
  const website = (form.website ?? "").trim().slice(0, CLUB_EDITABLE_LINK_FIELDS.website.maxLen);
  if (website) payload.website = website;
  getClubAuxiliaryLinkFieldKeys().forEach((key) => {
    const maxLen = CLUB_EDITABLE_LINK_FIELDS[key].maxLen;
    const v = (form[key] ?? "").trim().slice(0, maxLen);
    if (v) payload[key] = v;
  });
  const meetupUrlname = (form.meetup_urlname ?? "").trim();
  if (meetupUrlname) payload.meetup_urlname = meetupUrlname;
  const stravaClubId = (form.strava_club_id ?? "").trim();
  if (stravaClubId) payload.strava_club_id = stravaClubId.slice(0, 50);
  const regionVal = (form.region ?? "").trim();
  if (regionVal) payload.region = regionVal.slice(0, 120);
  return payload;
}

/**
 * Updater for Strava import + Meetup import club forms when a Google Place is chosen.
 * @param {object} place - GooglePlacesAddressInput place payload
 * @returns {(prev: object) => object}
 */
export function mergeGooglePlaceIntoClubForm(place) {
  const rawLat = place.latitude != null ? Number(place.latitude) : null;
  const rawLon = place.longitude != null ? Number(place.longitude) : null;
  const validCoords = rawLat != null && rawLon != null && Number.isFinite(rawLat) && Number.isFinite(rawLon);
  return (prev) => ({
    ...prev,
    home_base_address: place.formattedAddress ?? place.name ?? "",
    city: place.city || prev.city,
    state: place.state || prev.state,
    country: (place.country && String(place.country).trim()) || prev.country,
    latitude: validCoords ? rawLat : null,
    longitude: validCoords ? rawLon : null,
  });
}
